<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <div class="pricing-page">
    <!-- Hero Section with Animated Background -->
    <section class="pricing-hero">
      <!-- Animated background orbs -->
      <div class="hero-orbs">
        <div class="orb orb-1"></div>
        <div class="orb orb-2"></div>
        <div class="orb orb-3"></div>
      </div>

      <div class="container">
        <header class="pricing-header">
          <span class="pricing-badge">✨ {{ $t('pricing.badge') || 'Simple Pricing' }}</span>
          <h1>{{ $t('pricing.title') }}</h1>
          <p>{{ $t('pricing.description') }}</p>
        </header>
      </div>
    </section>

    <!-- Plans Section -->
    <section class="plans-section">
      <div class="container">
        <!-- Error Message -->
        <div v-if="subscriptionError" class="error-message">
          <div class="error-content">
            <span class="error-icon">⚠️</span>
            <span class="error-text">{{ subscriptionError }}</span>
            <button @click="subscriptionError = null" class="error-close">×</button>
          </div>
        </div>

        <PlanSelector
          :plans="plans"
          :current-plan="currentPlan"
          :is-loading="isLoading"
          recommended-plan="premium-yearly"
          @select-plan="selectPlan"
        />

        <!-- Trust Badges -->
        <div class="trust-section">
          <div class="trust-badges">
            <div class="trust-badge">
              <span class="trust-icon">🔒</span>
              <span>{{ $t('pricing.trust.secure') || 'Secure Payment' }}</span>
            </div>
            <div class="trust-badge">
              <span class="trust-icon">↩️</span>
              <span>{{ $t('pricing.trust.cancel') || 'Cancel Anytime' }}</span>
            </div>
            <div class="trust-badge">
              <span class="trust-icon">💬</span>
              <span>{{ $t('pricing.trust.support') || 'Priority Support' }}</span>
            </div>
          </div>
        </div>

        <div class="pricing-footer">
          <p>
            {{ $t('pricing.needHelp') }}
            <a href="mailto:contact@noorways.fr">{{ $t('pricing.contactTeam') }}</a>
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHead } from '@vueuse/head'
import { useRouter } from 'vue-router'
import { useSubscriptionStore } from '@/stores/subscription'
import { useAuthentication } from '@/authentication/AuthenticationManager'
import { createSubscriptionService } from '@/api/subscriptionService'
import type { SubscriptionPlan } from '@/types/subscription'
import stripeService from '@/services/stripeService'
import PlanSelector from '@/components/subscription/PlanSelector.vue'
import { logger } from '@/services/logger'

const router = useRouter()
const subscriptionStore = useSubscriptionStore()
const { getUserId, initializeAuthentication, isAuthenticated } = useAuthentication()
const { t } = useI18n()

// SEO and GEO optimization with structured data
useHead({
  title: () => `${t('pricing.title')} - n.writer`,
  meta: [
    {
      name: 'description',
      content: () => t('pricing.seo.description'),
    },
    {
      name: 'keywords',
      content: () => t('pricing.seo.keywords'),
    },
  ],
  script: [
    // BreadcrumbList Schema
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'n.writer',
            item: 'https://n-writer.app',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: t('pricing.title'),
            item: 'https://n-writer.app/pricing',
          },
        ],
      }),
    },
    // Product Schema with pricing offers
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'n.writer AI Writing Assistant',
        description: t('geo.definitions.noorwriter'),
        brand: {
          '@type': 'Brand',
          name: 'n.writer',
        },
        offers: [
          {
            '@type': 'Offer',
            name: t('pricing.plans.free.name'),
            price: '0',
            priceCurrency: 'EUR',
            description: t('geo.stats.freeUsage'),
            availability: 'https://schema.org/InStock',
          },
          {
            '@type': 'Offer',
            name: t('pricing.plans.premium.name') + ' Monthly',
            price: '4.90',
            priceCurrency: 'EUR',
            priceValidUntil: '2026-12-31',
            availability: 'https://schema.org/InStock',
          },
          {
            '@type': 'Offer',
            name: t('pricing.plans.premium.name') + ' Yearly',
            price: '39.90',
            priceCurrency: 'EUR',
            priceValidUntil: '2026-12-31',
            availability: 'https://schema.org/InStock',
          },
        ],
      }),
    },
    // FAQPage Schema for pricing FAQ
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: t('pricing.faq.free.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('pricing.faq.free.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('pricing.faq.payment.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('pricing.faq.payment.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('pricing.faq.cancel.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('pricing.faq.cancel.answer'),
            },
          },
        ],
      }),
    },
  ],
})

// Loading and error states for subscription processing
const isProcessingSubscription = ref(false)
const subscriptionError = ref<string | null>(null)

const plans = computed(() => subscriptionStore.plans)
const isLoading = computed(() => subscriptionStore.isLoading || isProcessingSubscription.value)
const currentPlan = computed(() => subscriptionStore.currentPlan)

onMounted(async () => {
  // Initialize authentication
  initializeAuthentication()

  // Always initialize pricing data (needed for both authenticated and unauthenticated users)
  try {
    await subscriptionStore.initializePricing()
  } catch (error) {
    logger.warn(
      'Failed to initialize pricing data in Pricing:',
      { error: error instanceof Error ? error.message : String(error) },
      'pricing'
    )
  }

  // Only initialize subscription data for authenticated users
  // Unauthenticated users can view pricing without needing live subscription data
  if (isAuthenticated.value) {
    try {
      await subscriptionStore.initializeSubscription()
    } catch (error) {
      logger.warn(
        'Failed to initialize subscription data in Pricing:',
        { error: error instanceof Error ? error.message : String(error) },
        'pricing'
      )
    }
  }
})

async function selectPlan(planId: string) {
  if (planId === 'free') {
    // Free plan cancellation requires authentication
    if (!isAuthenticated.value) {
      redirectToSignUp()
      return
    }
    await subscriptionStore.cancelSubscription()
  } else {
    // For premium plans, check authentication first
    if (!isAuthenticated.value) {
      redirectToSignUp()
      return
    }
    await handlePremiumSubscription(planId)
  }
}

/**
 * Redirect to sign-up page with current pricing page as redirect target
 * This makes more sense for pricing page visitors who are likely new customers
 */
function redirectToSignUp() {
  const currentPath = router.currentRoute.value.fullPath
  router.push(`/auth/signup?redirect=${encodeURIComponent(currentPath)}`)
}

/**
 * Handle premium subscription directly without redirect
 * Note: Authentication is already verified in selectPlan()
 */
async function handlePremiumSubscription(planId: string): Promise<void> {
  isProcessingSubscription.value = true
  subscriptionError.value = null

  try {
    // Verify user is authenticated (getUserId will throw if not authenticated)
    await getUserId()

    // Create subscription service and checkout session
    const subscriptionService = createSubscriptionService()
    const planType = planId.includes('yearly') ? 'yearly' : 'monthly'

    const result = await subscriptionService.createCheckoutSession(planType as SubscriptionPlan)

    if (result.success && result.sessionId) {
      // Redirect directly to Stripe checkout
      await redirectToStripeCheckout(result.sessionId)
    } else {
      subscriptionError.value = result.error || t('subscribe.error.checkoutFailed')
    }
  } catch (err) {
    logger.error(
      'Error processing subscription:',
      err instanceof Error ? err : undefined,
      { planId },
      'pricing'
    )
    // If authentication error occurs here, it means the user's session expired
    if (err instanceof Error && err.message.includes('authenticated')) {
      subscriptionError.value = t('subscribe.error.sessionExpired')
      // Redirect to sign-up after a short delay (user may need to re-register)
      setTimeout(() => redirectToSignUp(), 2000)
    } else {
      subscriptionError.value = t('subscribe.error.generic')
    }
  } finally {
    isProcessingSubscription.value = false
  }
}

/**
 * Redirect to Stripe checkout
 */
async function redirectToStripeCheckout(sessionId: string): Promise<void> {
  try {
    const result = await stripeService.redirectToCheckout(sessionId)

    if (!result.success) {
      subscriptionError.value = result.error || t('subscribe.error.stripeRedirectFailed')
    }
    // If successful, user will be redirected to Stripe (page will change)
  } catch (err) {
    logger.error(
      'Error redirecting to Stripe:',
      err instanceof Error ? err : undefined,
      { sessionId },
      'pricing'
    )
    subscriptionError.value = t('subscribe.error.generic')
  }
}
</script>

<style lang="scss" scoped>
.pricing-page {
  min-height: 100vh;
  background: var(--nw-bg-primary);
}

// Hero Section with Gradient
.pricing-hero {
  background: linear-gradient(135deg, var(--nw-brand-slate) 0%, #0f172a 100%);
  padding: 6rem 0 8rem;
  position: relative;
  overflow: hidden;
}

.hero-orbs {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.4;
  animation: float 20s ease-in-out infinite;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: var(--nw-primary);
  top: -100px;
  right: -100px;
  animation-delay: 0s;
}

.orb-2 {
  width: 300px;
  height: 300px;
  background: var(--nw-primary-light);
  bottom: -50px;
  left: 10%;
  animation-delay: -7s;
}

.orb-3 {
  width: 200px;
  height: 200px;
  background: #6366f1;
  top: 30%;
  right: 20%;
  animation-delay: -14s;
}

@keyframes float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  25% {
    transform: translate(30px, -30px) scale(1.05);
  }
  50% {
    transform: translate(-20px, 20px) scale(0.95);
  }
  75% {
    transform: translate(20px, 10px) scale(1.02);
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}

.pricing-header {
  text-align: center;
  max-width: 700px;
  margin: 0 auto;

  h1 {
    font-size: 3rem;
    font-weight: 800;
    color: var(--nw-white);
    margin-bottom: 1.5rem;
    line-height: 1.1;

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  p {
    font-size: 1.25rem;
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.6;
  }
}

.pricing-badge {
  display: inline-block;
  background: var(--nw-primary-10);
  color: var(--nw-primary-light);
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  border: 1px solid var(--nw-primary-20);
}

// Plans Section
.plans-section {
  padding: 4rem 0;
  margin-top: -4rem;
  position: relative;
  z-index: 2;
}

.error-message {
  max-width: 600px;
  margin: 0 auto 2rem;
  background: var(--nw-error-bg);
  border: 1px solid var(--nw-error);
  border-radius: var(--nw-radius-lg);
  padding: 1rem;
}

.error-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.error-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.error-text {
  flex: 1;
  color: var(--nw-error-dark);
  font-weight: 500;
}

.error-close {
  background: none;
  border: none;
  color: var(--nw-error-dark);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--nw-radius-sm);
  transition: background-color 0.2s;

  &:hover {
    background: var(--nw-error);
  }
}

// Trust Section
.trust-section {
  margin-top: 4rem;
}

.trust-badges {
  display: flex;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;
}

.trust-badge {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--nw-text-secondary);
  font-size: 0.9rem;
  font-weight: 500;

  .trust-icon {
    font-size: 1.25rem;
  }
}

.pricing-footer {
  text-align: center;
  margin-top: 4rem;
  padding-top: 2rem;
  border-top: 1px solid var(--nw-border-light);

  p {
    color: var(--nw-text-secondary);
  }

  a {
    color: var(--nw-primary);
    text-decoration: none;
    font-weight: 600;
    transition: color 0.2s;

    &:hover {
      color: var(--nw-primary-light);
      text-decoration: underline;
    }
  }
}

// Legacy styles (kept for reference, can be removed)
.pricing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 900px;
  margin: 0 auto;
}

.pricing-card {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;

  &.featured {
    border: 2px solid var(--nw-primary);
    transform: scale(1.05);

    &::before {
      content: 'Most Popular';
      position: absolute;
      top: -10px;
      left: 50%;
      transform: translateX(-50%);
      background: var(--nw-primary);
      color: var(--nw-text-inverse);
      padding: 0.5rem 1rem;
      border-radius: var(--nw-radius-xl);
      font-size: 0.875rem;
      font-weight: 600;
    }
  }
}

.plan-header {
  text-align: center;
  margin-bottom: 2rem;

  h3 {
    font-size: 1.5rem;
    color: var(--nw-text-primary);
    margin-bottom: 1rem;
  }
}

.plan-price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;

  .currency {
    font-size: 1.25rem;
    color: var(--nw-text-secondary);
  }

  .amount {
    font-size: 3rem;
    font-weight: 700;
    color: var(--nw-text-primary);
  }

  .interval {
    font-size: 1rem;
    color: var(--nw-text-secondary);
  }
}

.plan-features {
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;

  li {
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--nw-border-light);
    color: var(--nw-text-secondary);

    &:last-child {
      border-bottom: none;
    }

    &::before {
      content: '✓';
      color: var(--nw-success);
      font-weight: bold;
      margin-right: 0.5rem;
    }
  }
}

.plan-button {
  width: 100%;
  padding: 1rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &.btn-primary {
    background: var(--nw-primary);
    color: var(--nw-text-inverse);

    &:hover:not(:disabled) {
      background: var(--nw-primary-light);
    }
  }

  &.btn-secondary {
    background: var(--nw-secondary);
    color: var(--nw-text-inverse);

    &:hover:not(:disabled) {
      background: var(--nw-secondary-dark);
    }
  }

  &.btn-current {
    background: var(--nw-success);
    color: var(--nw-text-inverse);
    cursor: default;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
